import { Link, useParams } from 'react-router-dom'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { AlertCircle, Boxes, ClipboardList, Loader2, Package, Users, Wrench } from 'lucide-react'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { useKitDetail } from '@/hooks/usePickLists'
import { formatSheetDate, lotExpiryStatus } from '@/lib/pickList'
import { cn } from '@/lib/utils'
import type { KitComponent, KitDetail } from '@/types/pickList'

/**
 * One finished kit, as a record (`/inventory/kits/completed/:buildId`).
 *
 * Reached by clicking a row in Inventory → Kits → Kit Inventory. That row used
 * to open the kit RUN — the procedure page with its steps and Initiate button —
 * which answers "what do I do next" about work that is already finished. A
 * completed kit is a thing on a shelf; the question it answers is what went
 * into it, what made it, and who signed for it.
 *
 * Everything here is read-only by construction: the components were frozen at
 * End so a later lot edit cannot change what a released kit claims, and this
 * page renders that frozen record rather than recomputing it.
 */
// Per-user column layout for the kit's component list. Material is locked — a
// component row that does not name the material is unreadable. See
// src/components/table/index.ts for the pattern.
const KIT_COMPONENTS_TABLE_ID = 'kit-components'

const KIT_COMPONENTS_COLUMNS: TableColumnDef[] = [
  { id: 'material', header: 'Material', locked: true, sortable: true },
  { id: 'lot', header: 'Lot', sortable: true },
  { id: 'qty', header: 'Qty', sortable: true },
  { id: 'lotExpiry', header: 'Lot expiry', sortable: true },
]

// Header sorting (z8nrz7e4h6). Unsorted, components keep the frozen record's order.
const KIT_COMPONENTS_SORT: SortAccessors<KitComponent> = {
  material: c => c.materialName,
  lot: c => c.lotNumber,
  qty: c => c.qty,
  lotExpiry: c => c.expiryDate,
}

export default function KitDetailPage() {
  const { buildId } = useParams<{ buildId: string }>()
  const { data: kit, isLoading, error } = useKitDetail(buildId)

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='px-6 py-8'>
        <Breadcrumbs
          items={[
            { label: 'Inventory', to: '/inventory?tab=kits' },
            { label: kit?.kitSupplyId ?? 'Kit' },
          ]}
          className='mb-4'
        />

        {isLoading ? (
          <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading kit…
          </div>
        ) : error || !kit ? (
          <div className='flex items-center gap-2 rounded-xl border bg-card p-8 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4' />
            {error instanceof Error ? error.message : 'Could not load this kit.'}
          </div>
        ) : (
          <KitRecord kit={kit} />
        )}
      </div>
    </div>
  )
}

function KitRecord({ kit }: { kit: KitDetail }) {
  const cols = useTableColumns(KIT_COMPONENTS_TABLE_ID, KIT_COMPONENTS_COLUMNS)
  const components = useSortedRows(kit.components, cols.sort, KIT_COMPONENTS_SORT)
  const exp = lotExpiryStatus(kit.kitExpiry)
  return (
    <div className='space-y-4'>
      <header className='rounded-xl border bg-card p-5'>
        <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
          <h1 className='font-mono text-xl font-semibold tracking-tight'>{kit.kitSupplyId}</h1>
          <span className='text-lg text-muted-foreground'>{kit.kitName}</span>
          <span
            className={cn(
              'ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium',
              kit.status === 'COMPLETED'
                ? 'bg-green-50 text-green-700'
                : 'bg-amber-50 text-amber-700',
            )}
          >
            {kit.status === 'COMPLETED' ? 'Completed' : kit.status.toLowerCase()}
          </span>
        </div>

        <dl className='mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4'>
          <Fact label='Kit expiry' hint='earliest component expiry, frozen at build'>
            <span className='font-medium tabular-nums'>
              {kit.kitExpiry ? formatSheetDate(kit.kitExpiry) : 'None'}
            </span>
            <ExpiryBadge status={exp} />
          </Fact>
          <Fact label='Built'>{kit.builtAt ? formatSheetDate(kit.builtAt) : '—'}</Fact>
          <Fact label='Kitted by'>{kit.kittedByName ?? 'Not recorded'}</Fact>
          <Fact label='Verified by'>{kit.verifiedByName ?? 'Not recorded'}</Fact>
        </dl>

        {kit.procedureTemplateName && (
          <p className='mt-4 border-t pt-3 text-xs text-muted-foreground'>
            Built from <span className='font-medium'>{kit.procedureTemplateName}</span> ·{' '}
            {/* The run is still reachable — it is just not what this page is for. */}
            <Link to={`/kits/runs/${kit.procedureId}`} className='underline hover:text-foreground'>
              open the kit run
            </Link>
          </p>
        )}
      </header>

      <Panel
        icon={<Boxes className='h-4 w-4' />}
        title='Components'
        count={kit.components.length}
        empty='No components were recorded against this kit.'
      >
        <table className='w-full min-w-[560px] text-sm'>
          <thead>
            <tr className='border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground'>
              {cols.visible.map(({ def }) => (
                <ResizableTh
                  key={def.id}
                  columnId={def.id}
                  columns={cols}
                  label={def.header}
                  className={
                    def.id === 'qty'
                      ? 'px-4 py-2 text-right font-semibold'
                      : 'px-4 py-2 text-left font-semibold'
                  }
                >
                  {def.header}
                </ResizableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {components.map((c, i) => {
              const lot = lotExpiryStatus(c.expiryDate)
              return (
                <tr key={`${c.lotNumber}-${i}`} className='border-b last:border-0'>
                  <ColumnCells
                    columns={cols}
                    cells={{
                      material: (
                        <td className='px-4 py-2.5'>
                          <span className='font-medium'>{c.materialName}</span>
                          <span className='ml-2 font-mono text-xs text-muted-foreground'>
                            {c.materialCode}
                          </span>
                        </td>
                      ),
                      lot: <td className='px-4 py-2.5 font-mono text-xs'>{c.lotNumber}</td>,
                      qty: <td className='px-4 py-2.5 text-right tabular-nums'>{c.qty}</td>,
                      lotExpiry: (
                        <td className='px-4 py-2.5 tabular-nums'>
                          {c.expiryDate ? formatSheetDate(c.expiryDate) : '—'}
                          <ExpiryBadge status={lot} />
                        </td>
                      ),
                    }}
                  />
                </tr>
              )
            })}
          </tbody>
        </table>
      </Panel>

      <div className='grid gap-4 lg:grid-cols-2'>
        <Panel
          icon={<Wrench className='h-4 w-4' />}
          title='Equipment'
          count={kit.equipment.length}
          empty='No equipment was configured for this kit’s step.'
        >
          <ul className='divide-y'>
            {kit.equipment.map((e, i) => (
              <li key={i} className='flex items-baseline justify-between gap-3 px-4 py-2.5 text-sm'>
                <span className='text-muted-foreground'>{e.requirementLabel}</span>
                {/* A configured requirement with no unit against it is a gap in
                    the record, and saying so is the point — rendering it blank
                    would look like the row simply had no second column. */}
                {e.equipmentLabel ? (
                  <span className='font-medium'>{e.equipmentLabel}</span>
                ) : (
                  <span className='text-xs italic text-muted-foreground/70'>Not recorded</span>
                )}
              </li>
            ))}
          </ul>
        </Panel>

        <Panel
          icon={<Users className='h-4 w-4' />}
          title='Staffing'
          count={kit.staffing.length}
          empty='No staffing was configured for this kit’s step.'
        >
          <ul className='divide-y'>
            {kit.staffing.map((s, i) => (
              <li key={i} className='flex items-baseline justify-between gap-3 px-4 py-2.5 text-sm'>
                <span className='font-medium'>{s.roleLabel}</span>
                <span className='tabular-nums text-muted-foreground'>
                  {s.numResources} × {s.totalHrs} h
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel
        icon={<ClipboardList className='h-4 w-4' />}
        title={kit.form ? kit.form.name : 'Kit form'}
        count={kit.form?.values.length}
        empty={
          kit.form
            ? 'The form has no recorded answers — its only field is the process step.'
            : 'This kit has no form submission.'
        }
      >
        <dl className='divide-y'>
          {(kit.form?.values ?? []).map(v => (
            <div key={v.fieldId} className='grid gap-1 px-4 py-2.5 text-sm sm:grid-cols-[220px_1fr]'>
              <dt className='text-muted-foreground'>{v.label}</dt>
              <dd className='font-medium'>{v.value}</dd>
            </div>
          ))}
        </dl>
      </Panel>
    </div>
  )
}

/**
 * `lotExpiryStatus.cls` is a badge BACKGROUND, so it has to be worn by a badge —
 * applied as a text colour it renders light amber on white.
 */
function ExpiryBadge({ status }: { status: ReturnType<typeof lotExpiryStatus> }) {
  if (!status || (!status.expired && !status.nearExpiry)) return null
  return (
    <span
      className={cn('ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-medium', status.cls)}
    >
      {status.text}
    </span>
  )
}

function Fact({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <dt className='text-[11px] uppercase tracking-wider text-muted-foreground'>{label}</dt>
      <dd className='mt-0.5 text-sm'>{children}</dd>
      {hint && <p className='text-[11px] italic text-muted-foreground/70'>{hint}</p>}
    </div>
  )
}

/**
 * A section that states its own emptiness. "No equipment was configured" and a
 * blank box look identical otherwise, and only one of them means anything.
 */
function Panel({
  icon,
  title,
  count,
  empty,
  children,
}: {
  icon: React.ReactNode
  title: string
  count?: number
  empty: string
  children: React.ReactNode
}) {
  return (
    <section className='overflow-hidden rounded-xl border bg-card'>
      <h2 className='flex items-center gap-2 border-b px-4 py-3 text-sm font-semibold'>
        <span className='text-muted-foreground'>{icon}</span>
        {title}
        {count !== undefined && count > 0 && (
          <span className='tabular-nums text-muted-foreground'>{count}</span>
        )}
      </h2>
      {count ? (
        <div className='overflow-x-auto'>{children}</div>
      ) : (
        <p className='flex items-center gap-2 px-4 py-6 text-xs text-muted-foreground'>
          <Package className='h-4 w-4 text-muted-foreground/40' />
          {empty}
        </p>
      )}
    </section>
  )
}
